/**
 * IdArrayShrinker - Main class for shrinking/expanding ID arrays.
 */

import { strategies } from "./strategies";
import { ShrinkType, ShrunkIdArray } from "./types";

/**
 * Estimates the memory size of a ShrunkIdArray in bytes.
 * @param shrunk - The shrunk ID array to measure
 * @returns Estimated memory footprint in bytes
 */
export function estimateShrunkSize(shrunk: ShrunkIdArray): number {
    const baseOverhead = 32; // Object overhead + properties
    if (shrunk.data instanceof Uint8Array) {
        return baseOverhead + shrunk.data.byteLength;
    }
    return baseOverhead + shrunk.data.length * 8;
}

export class IdArrayShrinker {
    /**
     * Shrink an array of IDs using the specified mode, or auto-detect the best mode.
     *
     * @param ids - Array of distinct, sorted IDs
     * @param mode - Optional shrink mode. If not provided, auto-detects the best mode.
     * @returns ShrunkIdArray that can be expanded back to the original
     */
    static shrink(ids: number[], mode?: ShrinkType): ShrunkIdArray {
        if (ids.length === 0) {
            return { type: ShrinkType.None, data: [], length: 0 };
        }

        const selectedMode = mode ?? this.detectBestMode(ids);
        return strategies[selectedMode].shrink(ids);
    }

    /**
     * Expand a ShrunkIdArray back to the original number array.
     *
     * @param shrunk - The shrunk ID array
     * @returns Original array of IDs
     */
    static expand(shrunk: ShrunkIdArray): number[] {
        return strategies[shrunk.type].expand(shrunk);
    }

    /**
     * Auto-detect the best shrink mode based on data characteristics.
     * Estimates memory usage for each mode and picks the smallest.
     * @param ids - Array of distinct, sorted IDs
     * @returns The optimal ShrinkType for this data
     */
    static detectBestMode(ids: number[]): ShrinkType {
        if (ids.length === 0) {
            return ShrinkType.None;
        }

        const minId = ids[0];
        const maxId = ids[ids.length - 1];

        // Estimate sizes for each mode
        const sizes = Object.entries(strategies).map(([type, strategy]) => ({
            type: type as ShrinkType,
            size: strategy.estimateSize(ids, minId, maxId),
        }));

        // Pick the smallest
        return sizes.reduce((best, current) => (current.size < best.size ? current : best)).type;
    }
}
